import React, { memo, useCallback, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useQuestionnaireManagement } from "src-next/modules/questionnaire-builder/questionnaire-management/hooks";
import type { QuestionnairePayload } from "src-next/modules/questionnaire-builder/form-builder/types";
import { isQuestionnaireSelectableForClinicalUse } from "@/utils/questionnaireClinicalSelection";
import type { VetProtocolQuestionnaireItem } from "@/fluxvet/hooks/usePrescriptionTemplates";

export interface VetProtocolQuestionnairesPickerProps {
  /** Lista atual (controle externo: react-hook-form ou estado local). */
  value: VetProtocolQuestionnaireItem[];
  /** Atualiza a lista completa após toggle. */
  onChange: (next: VetProtocolQuestionnaireItem[]) => void;
}

/**
 * Lista questionários da organização e permite marcar/desmarcar para protocolo/atendimento.
 * Mesmas regras de `QuestionnairesTabPanel` (pós-consulta + selecionável clinicamente).
 *
 * `useQuestionnaireManagement` normaliza o ID da organização internamente
 * (`ensureOrganizationIdWithOidPrefix`); usamos `organizationId` do contexto
 * (mesmo valor numérico que o Footer usa para envio de tasks).
 */
const VetProtocolQuestionnairesPicker = memo(
  ({ value, onChange }: VetProtocolQuestionnairesPickerProps) => {
    const navigate = useNavigate();
    const { organizationId } = useOrganizationContext();

    const { questionnaires, isLoading, errorMessage } =
      useQuestionnaireManagement({
        organizationId: organizationId || undefined,
      });

    const selectedIds = useMemo(() => {
      return new Set(
        value
          .map((item) => item.questionnaireId)
          .filter((id) => typeof id === "string" && id.length > 0),
      );
    }, [value]);

    const filteredQuestionnaires = useMemo(
      () =>
        questionnaires.filter(
          (q) =>
            q.usageType === "postConsultation" &&
            isQuestionnaireSelectableForClinicalUse(q),
        ),
      [questionnaires],
    );

    const toggleSelection = useCallback(
      (questionnaire: QuestionnairePayload) => {
        const questionnaireId = questionnaire.id?.trim();
        if (!questionnaireId) return;
        if (!isQuestionnaireSelectableForClinicalUse(questionnaire)) return;

        const exists = value.some((v) => v.questionnaireId === questionnaireId);
        if (exists) {
          onChange(
            value.filter((v) => v.questionnaireId !== questionnaireId),
          );
          return;
        }

        const item: VetProtocolQuestionnaireItem = {
          type: "questionnaire",
          questionnaireId,
          title: questionnaire.title,
          name: questionnaire.title,
          label: questionnaireId,
          ...(questionnaire.usageType
            ? { usageType: questionnaire.usageType }
            : {}),
        };
        onChange([...value, item]);
      },
      [onChange, value],
    );

    return (
      <Stack width="100%" spacing={1.5}>
        <Button
          sx={{ width: "fit-content", textTransform: "none" }}
          startIcon={<Search />}
          onClick={() => navigate("/questionnaireBuilder")}
        >
          Criar / editar questionários
        </Button>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Questionários de pós-consulta (mesmos filtros da consulta humana)
          </Typography>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Box>
        <List
          dense
          disablePadding
          sx={{
            borderRadius: 1,
            borderColor: "divider",
            maxHeight: 360,
            overflow: "auto",
          }}
        >
          {isLoading ? (
            <ListItem>
              <ListItemText
                primary="Carregando questionários..."
                secondary="Aguarde alguns segundos."
              />
            </ListItem>
          ) : filteredQuestionnaires.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="Nenhum questionário encontrado."
                secondary="Crie um questionário de pós-consulta ou verifique a organização."
              />
            </ListItem>
          ) : (
            filteredQuestionnaires.map((questionnaire) => {
              const qid = questionnaire.id?.trim();
              const isSelected = qid ? selectedIds.has(qid) : false;
              return (
                <ListItem
                  key={qid || questionnaire.title}
                  secondaryAction={
                    <Checkbox
                      edge="end"
                      checked={isSelected}
                      onChange={() => toggleSelection(questionnaire)}
                    />
                  }
                >
                  <ListItemButton onClick={() => toggleSelection(questionnaire)}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500}>
                          {questionnaire.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Pós-consulta
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })
          )}
        </List>
      </Stack>
    );
  },
);

VetProtocolQuestionnairesPicker.displayName = "VetProtocolQuestionnairesPicker";

export default VetProtocolQuestionnairesPicker;
